export const modeTileContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

export const reviewHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 20px',
  borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
  background: ({ palette }) =>
    `linear-gradient(180deg, ${palette.purple.extraSoft}, ${palette.background.paper})`,
};

export const reviewHeaderAiBadgeStyles = {
  width: '38px',
  height: '38px',
  borderRadius: '10px',
  backgroundColor: ({ palette }) => palette.purple.main,
  color: ({ palette }) => palette.common.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const reviewHeaderInfoStyles = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

export const reviewHeaderActionsStyles = {
  marginLeft: 'auto',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
};

export const reviewNeedsReviewInlineStyles = {
  color: ({ palette }) => (palette.mode === 'dark' ? palette.warning.main : palette.warning.dark),
};

export const reviewCompletionBannerStyles = {
  display: 'block',
  mt: '2px',
  color: ({ palette }) => (palette.mode === 'dark' ? palette.warning.main : palette.warning.dark),
};

export const reviewToolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 20px',
  borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
  backgroundColor: ({ palette }) => palette.background.secondary,
};

export const reviewFilterSegmentStyles = {
  display: 'flex',
  border: ({ palette }) => `1px solid ${palette.divider}`,
  borderRadius: '8px',
  overflow: 'hidden',
  marginLeft: 'auto',
};

export const getReviewFilterSegmentItemStyles = isActive => ({
  padding: '6px 12px',
  cursor: 'pointer',
  backgroundColor: isActive
    ? ({ palette }) => palette.primary.main
    : ({ palette }) => palette.background.paper,
  color: isActive ? ({ palette }) => palette.common.white : ({ palette }) => palette.text.secondary,
  userSelect: 'none',
});

export const drawerContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  // Prevent any child from forcing a horizontal scrollbar on the drawer.
  overflowX: 'hidden',
};

export const reviewScrollAreaStyles = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: 0,
  // Guarantee the scroll area never forces a horizontal scroll track even if a
  // child (e.g. a very long document-type name) doesn't respect min-width: 0.
  minWidth: 0,
};

export const segmentRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '12px 20px',
  borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
  // minWidth: 0 + overflow: hidden ensure the row fits within the drawer column at any
  // drawer width without pushing the container wider (DEV-4554 horizontal scroll fix).
  minWidth: 0,
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: ({ palette }) => palette.background.secondary,
  },
};

export const segmentRowLowConfidenceStyles = {
  ...segmentRowStyles,
  // A soft, low-alpha amber tint flags the row without swamping its text. The previous LIGHT fill
  // (warning.light = #FEC84B, a saturated amber) washed out the muted source line, the italic
  // "Unrecognized" title, and the "edited" badge — none reached AA contrast. warning.soft (#FEEFC6
  // light / low-alpha amber dark) keeps the flag legible while the dark ink on it reads clearly.
  backgroundColor: ({ palette }) => palette.warning.soft ?? '#FFFAEB',
  // Override the spread hover: low-confidence rows keep the amber background on hover.
  '&:hover': {
    backgroundColor: ({ palette }) => palette.warning.soft ?? '#FFFAEB',
    opacity: 0.9,
  },
};

export const segmentInfoCellStyles = {
  minWidth: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  // overflow:hidden allows text-overflow:ellipsis on children to work within this flex item.
  overflow: 'hidden',
};

export const segmentNameTypographyStyles = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const segmentTypeSelectorStyles = {
  // DEV-4554: bounded width so the selector doesn't push the row wider than the drawer.
  // At DrawerSize.LARGE (920px) the row has ~760px after drawer padding; the selector
  // gets a generous slice while the info cell (flex:1 + minWidth:0) takes the rest.
  minWidth: '180px',
  maxWidth: '260px',
  flexShrink: 0,
};

export {
  confidenceBadgeHighStyles as segmentConfidenceBadgeHighStyles,
  confidenceBadgeLowStyles as segmentConfidenceBadgeLowStyles,
  confidenceBadgeNeutralStyles as segmentConfidenceBadgeNeutralStyles,
} from '../documentsConfidence.styles';

export const splitDividerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '4px 20px 4px 96px',
  color: ({ palette }) => palette.purple.main,
};

export const splitDividerLineStyles = {
  flex: 1,
  height: '1px',
  background: 'repeating-linear-gradient(90deg, currentColor 0 6px, transparent 6px 12px)',
};

export const splitDividerChipStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  border: ({ palette }) => `1px solid ${palette.purple.light}`,
  backgroundColor: ({ palette }) => palette.purple.extraSoft,
  borderRadius: '14px',
  padding: '2px 8px',
  cursor: 'pointer',
  color: ({ palette }) => palette.purple.main,
  '&:hover': {
    backgroundColor: ({ palette }) => palette.purple.soft,
  },
};

export const reviewFooterStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  borderTop: ({ palette }) => `1px solid ${palette.divider}`,
  backgroundColor: ({ palette }) => palette.background.secondary,
};

export const reviewFooterActionsStyles = {
  marginLeft: 'auto',
  display: 'flex',
  gap: '10px',
};

export const confirmButtonIconStyle = { marginRight: '6px' };

export const reviewConfirmButtonStyles = {
  backgroundColor: ({ palette }) => palette.purple.main,
  color: ({ palette }) => palette.common.white,
  '&:hover': {
    backgroundColor: ({ palette }) => palette.purple.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: ({ palette }) => palette.purple.light,
    color: ({ palette }) => palette.common.white,
  },
};

export const segmentUnrecognizedStyles = {
  // text.disabled (light GRAY[5] #D0D5DD) washed out on the flagged amber row (and even on paper).
  // text.secondary keeps the muted, italic "unrecognized" feel while clearing AA on both surfaces.
  color: ({ palette }) => palette.text.secondary,
  fontStyle: 'italic',
};

export const modeTilesRowStyles = {
  display: 'flex',
  gap: '10px',
  justifyContent: 'center',
  mt: '16px',
};

export const modeTileStyles = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  backgroundColor: ({ palette }) => palette.background.paper,
  border: ({ palette }) => `1px solid ${palette.divider}`,
  borderRadius: '10px',
  padding: '10px 12px',
  textAlign: 'left',
  minWidth: '152px',
  maxWidth: '180px',
};

// ── Segment preview & boundary editing ───────────────────────────────────────

export const segmentPreviewRowStyles = {
  padding: '4px 20px 8px 96px',
};

export const segmentActionBarStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 20px 10px 96px',
  flexWrap: 'wrap',
};

export const previewButtonStyles = {
  borderColor: ({ palette }) => palette.text.secondary,
  color: ({ palette }) => palette.text.secondary,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => palette.background.secondary,
    borderColor: ({ palette }) => palette.text.primary,
  },
};

export const splitButtonStyles = {
  borderColor: ({ palette }) => palette.warning.main,
  color: ({ palette }) => palette.warning.dark,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => palette.warning.light,
  },
};

export const mergeButtonStyles = {
  borderColor: ({ palette }) => palette.primary.light,
  color: ({ palette }) => palette.primary.main,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => `${palette.primary.main}14`,
  },
};

export const removeButtonStyles = {
  borderColor: ({ palette }) => palette.error.light,
  color: ({ palette }) => palette.error.main,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => `${palette.error.main}0F`,
  },
};

export const reclassifyButtonStyles = {
  borderColor: ({ palette }) => palette.purple.light,
  color: ({ palette }) => palette.purple.main,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => palette.purple.extraSoft,
  },
};

export const restoreButtonStyles = {
  borderColor: ({ palette }) => palette.success.main,
  color: ({ palette }) => palette.success.main,
  borderRadius: '14px',
  padding: '2px 10px',
  minWidth: 0,
  '&:hover': {
    backgroundColor: ({ palette }) => `${palette.success.main}14`,
  },
};

export const segmentRemovedRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '8px 20px',
  borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
  opacity: 0.5,
  minWidth: 0,
  overflow: 'hidden',
};

export const editedBadgeStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  border: ({ palette }) => `1px solid ${palette.warning.main}`,
  // The row it sits on is already a soft amber tint; filling the badge with warning.light (a solid
  // amber) made it amber-on-amber with washed-out warning.dark text. An extraSoft/paper fill gives
  // the deep-amber label text a high-contrast backing so the "edited · pp" badge stays legible.
  backgroundColor: ({ palette }) => palette.warning.extraSoft ?? palette.background.paper,
  borderRadius: '10px',
  padding: '1px 6px',
  color: ({ palette }) => palette.warning.dark,
  whiteSpace: 'nowrap',
};

export const groupingChipRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  mt: '2px',
};

export const groupingChipStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  border: ({ palette }) => `1px solid ${palette.purple.light}`,
  backgroundColor: ({ palette }) => palette.purple.extraSoft,
  borderRadius: '10px',
  padding: '1px 6px',
  color: ({ palette }) => palette.purple.main,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

export const contributingFileNamesStyles = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

// ── Split-here picker ─────────────────────────────────────────────────────────

export const splitPickerBannerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 20px 6px 96px',
  backgroundColor: ({ palette }) => palette.warning.light ?? '#FFFAEB',
  borderTop: ({ palette }) => `1px solid ${palette.warning.main}`,
  borderBottom: ({ palette }) => `1px solid ${palette.warning.main}`,
};

export const splitPickerPageStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexWrap: 'wrap',
};

export const getSplitPageChipStyles = isSelected => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '28px',
  padding: '2px 6px',
  borderRadius: '6px',
  cursor: 'pointer',
  border: isSelected
    ? ({ palette }) => `2px solid ${palette.warning.main}`
    : ({ palette }) => `1px solid ${palette.divider}`,
  backgroundColor: isSelected
    ? ({ palette }) => palette.warning.main
    : ({ palette }) => palette.background.paper,
  color: isSelected ? ({ palette }) => palette.common.white : ({ palette }) => palette.text.primary,
  '&:hover': {
    borderColor: ({ palette }) => palette.warning.main,
  },
});

// ── Footer tally (DEV-4555) ───────────────────────────────────────────────────

export const reviewFooterTallyStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
};

export const modeTileIconStyles = {
  width: '30px',
  height: '30px',
  borderRadius: '8px',
  backgroundColor: ({ palette }) => palette.purple.soft,
  // Light: dark-purple glyph on a pale-purple tile. Dark: the tile is a subtle dark tint, so the
  // glyph must be a LIGHT purple to stay legible.
  color: ({ palette }) => (palette.mode === 'dark' ? palette.purple.light : palette.purple.dark),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: '2px',
};
